import React, { useCallback, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../store/hooks";
import { selectors } from "../../../store/selectors/shipBuilderSelectors";
import { addDeckPlan, removeDeckPlan, updateDeckPlan } from "../../../store/features/shipBuilderSlice";

interface DeckPlanUploadProps {
  className?: string;
}

interface DeckPlanUploadState {
  id: string;
  name: string;
  description: string;
  isDragOver: boolean;
  isLoading: boolean;
  image: string | null;
}

export const DeckPlanUpload: React.FC<DeckPlanUploadProps> = ({ className = "" }) => {
  const dispatch = useAppDispatch();
  const deckplans = useAppSelector(selectors.selectFinalDetails)?.deckplans || [];
  const [uploadStates, setUploadStates] = useState<DeckPlanUploadState[]>([
    {
      id: "1",
      name: "",
      description: "",
      isDragOver: false,
      isLoading: false,
      image: null,
    }
  ]);

  const handleImageUpload = useCallback((file: File, uploadId: string) => {
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file.');
      return;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB limit for deckplans
      alert('Deckplan image file size must be less than 10MB.');
      return;
    }

    setUploadStates(prev => prev.map(state => 
      state.id === uploadId 
        ? { ...state, isLoading: true }
        : state
    ));

    const reader = new FileReader();
    
    reader.onload = (e) => {
      const result = e.target?.result as string;
      if (result) {
        setUploadStates(prev => prev.map(state => 
          state.id === uploadId 
            ? { ...state, image: result, isLoading: false }
            : state
        ));
      }
    };

    reader.onerror = () => {
      alert('Error reading image file.');
      setUploadStates(prev => prev.map(state => 
        state.id === uploadId 
          ? { ...state, isLoading: false }
          : state
      ));
    };

    reader.readAsDataURL(file);
  }, []);

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>, uploadId: string) => {
    const file = event.target.files?.[0];
    if (file) {
      handleImageUpload(file, uploadId);
    }
  }, [handleImageUpload]);

  const handleDragOver = useCallback((event: React.DragEvent, uploadId: string) => {
    event.preventDefault();
    setUploadStates(prev => prev.map(state => 
      state.id === uploadId 
        ? { ...state, isDragOver: true }
        : state
    ));
  }, []);

  const handleDragLeave = useCallback((event: React.DragEvent, uploadId: string) => {
    event.preventDefault();
    setUploadStates(prev => prev.map(state => 
      state.id === uploadId 
        ? { ...state, isDragOver: false }
        : state
    ));
  }, []);

  const handleDrop = useCallback((event: React.DragEvent, uploadId: string) => {
    event.preventDefault();
    setUploadStates(prev => prev.map(state => 
      state.id === uploadId 
        ? { ...state, isDragOver: false }
        : state
    ));
    
    const files = event.dataTransfer.files;
    if (files.length > 0) {
      handleImageUpload(files[0], uploadId);
    }
  }, [handleImageUpload]);

  const handleSaveDeckPlan = useCallback((uploadId: string) => {
    const uploadState = uploadStates.find(state => state.id === uploadId);
    if (!uploadState || !uploadState.image || !uploadState.name.trim()) {
      alert('Please provide a name and upload an image for the deckplan.');
      return;
    }

    dispatch(addDeckPlan({
      name: uploadState.name.trim(),
      image: uploadState.image,
      description: uploadState.description.trim() || undefined,
    }));

    // Reset the upload state
    setUploadStates(prev => prev.map(state => 
      state.id === uploadId 
        ? { ...state, name: "", description: "", image: null }
        : state
    ));
  }, [dispatch, uploadStates]);

  const handleRemoveDeckPlan = useCallback((deckplanId: string) => {
    dispatch(removeDeckPlan(deckplanId));
  }, [dispatch]);

  const handleUpdateDeckPlan = useCallback((deckplanId: string, field: 'name' | 'description', value: string) => {
    dispatch(updateDeckPlan({ id: deckplanId, [field]: value }));
  }, [dispatch]);

  const handleAddUploadArea = useCallback(() => {
    const newId = (uploadStates.length + 1).toString();
    setUploadStates(prev => [...prev, {
      id: newId,
      name: "",
      description: "",
      isDragOver: false,
      isLoading: false,
      image: null,
    }]);
  }, [uploadStates.length]);

  const handleRemoveUploadArea = useCallback((uploadId: string) => {
    if (uploadStates.length > 1) {
      setUploadStates(prev => prev.filter(state => state.id !== uploadId));
    }
  }, [uploadStates.length]);

  return (
    <div className={`space-y-6 ${className}`}>
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-800">Deckplans</h3>
        <button
          onClick={handleAddUploadArea}
          className="px-3 py-1 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 transition-colors"
        >
          Add Deckplan Upload
        </button>
      </div>

      {/* Existing Deckplans */}
      {deckplans.length > 0 && (
        <div className="space-y-4">
          <h4 className="text-md font-medium text-gray-700">Uploaded Deckplans</h4>
          {deckplans.map((deckplan) => (
            <div key={deckplan.id} className="border border-gray-300 rounded-lg p-4 bg-gray-50">
              <div className="flex items-start gap-4">
                <img
                  src={deckplan.image}
                  alt={deckplan.name}
                  className="w-32 h-24 object-contain border border-gray-300 rounded bg-white"
                />
                <div className="flex-1 space-y-2">
                  <div>
                    <input
                      type="text"
                      value={deckplan.name}
                      onChange={(e) => handleUpdateDeckPlan(deckplan.id, 'name', e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm font-medium"
                      placeholder="Deckplan name"
                    />
                  </div>
                  <div>
                    <textarea
                      value={deckplan.description || ""}
                      onChange={(e) => handleUpdateDeckPlan(deckplan.id, 'description', e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-sm resize-none"
                      rows={2}
                      placeholder="Description (optional)"
                    />
                  </div>
                  <button
                    onClick={() => handleRemoveDeckPlan(deckplan.id)}
                    className="px-2 py-1 bg-red-500 text-white rounded text-xs hover:bg-red-600 transition-colors"
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Upload Areas */}
      <div className="space-y-4">
        <h4 className="text-md font-medium text-gray-700">Upload New Deckplans</h4>
        {uploadStates.map((uploadState) => (
          <div key={uploadState.id} className="border border-gray-300 rounded-lg p-4">
            <div className="flex items-center justify-between mb-3">
              <h5 className="font-medium text-gray-800">Deckplan {uploadState.id}</h5>
              {uploadStates.length > 1 && (
                <button
                  onClick={() => handleRemoveUploadArea(uploadState.id)}
                  className="px-2 py-1 bg-gray-500 text-white rounded text-xs hover:bg-gray-600 transition-colors"
                >
                  Remove Upload Area
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Image Upload */}
              <div>
                {uploadState.image ? (
                  <div className="space-y-2">
                    <img
                      src={uploadState.image}
                      alt="Deckplan preview"
                      className="w-full h-48 object-contain border border-gray-300 rounded bg-gray-50"
                    />
                    <button
                      onClick={() => setUploadStates(prev => prev.map(state => 
                        state.id === uploadState.id 
                          ? { ...state, image: null }
                          : state
                      ))}
                      className="px-2 py-1 bg-gray-500 text-white rounded text-xs hover:bg-gray-600 transition-colors"
                    >
                      Remove Image
                    </button>
                  </div>
                ) : (
                  <div
                    className={`border-2 border-dashed rounded-lg p-4 text-center transition-colors h-48 flex items-center justify-center ${
                      uploadState.isDragOver
                        ? "border-blue-400 bg-blue-50"
                        : "border-gray-300 hover:border-gray-400"
                    }`}
                    onDragOver={(e) => handleDragOver(e, uploadState.id)}
                    onDragLeave={(e) => handleDragLeave(e, uploadState.id)}
                    onDrop={(e) => handleDrop(e, uploadState.id)}
                  >
                    {uploadState.isLoading ? (
                      <div className="space-y-2">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                        <p className="text-sm text-gray-600">Processing image...</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        <div className="text-gray-400">
                          <svg className="mx-auto h-8 w-8" stroke="currentColor" fill="none" viewBox="0 0 48 48">
                            <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </div>
                        <div>
                          <label htmlFor={`deckplan-upload-${uploadState.id}`} className="cursor-pointer">
                            <span className="text-blue-600 hover:text-blue-500 font-medium text-sm">
                              Click to upload
                            </span>
                            <span className="text-gray-500 text-sm"> or drag and drop</span>
                          </label>
                          <input
                            id={`deckplan-upload-${uploadState.id}`}
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleFileSelect(e, uploadState.id)}
                            className="hidden"
                          />
                        </div>
                        <p className="text-xs text-gray-500">
                          PNG, JPG, GIF up to 10MB
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Details Form */}
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Deckplan Name *
                  </label>
                  <input
                    type="text"
                    value={uploadState.name}
                    onChange={(e) => setUploadStates(prev => prev.map(state => 
                      state.id === uploadState.id 
                        ? { ...state, name: e.target.value }
                        : state
                    ))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-300"
                    placeholder="e.g., Main Deck, Bridge Deck"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={uploadState.description}
                    onChange={(e) => setUploadStates(prev => prev.map(state => 
                      state.id === uploadState.id 
                        ? { ...state, description: e.target.value }
                        : state
                    ))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-300 resize-none"
                    rows={3}
                    placeholder="Optional description of this deckplan"
                  />
                </div>
                <button
                  onClick={() => handleSaveDeckPlan(uploadState.id)}
                  disabled={!uploadState.image || !uploadState.name.trim()}
                  className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                >
                  Save Deckplan
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}; 